import { useMemo, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BillingState,
  GetProgramEnrollmentPaymentPlansQuery,
  PaymentPlanStatus,
} from '@sandtech-hub-monorepo/ehub-client';

import { Button, cn, TimeUnit } from '@sandtech-hub-monorepo/ui-kit';
import { AlertCircle, CheckCircle, ChevronRight, Info } from 'lucide-react';
import { ExternalApplicationLinks } from '../../../utils';
import { formatDate } from '../../dateFormat';

type AlertLevel = 'blue' | 'amber' | 'red' | 'success' | null;

// Context passed to message generator functions
type PaymentMessageContext = {
  formattedDueDate: string;
  formattedOverdueDate: string;
  instalmentAmount: number;
  currentSemesterRemaining: number;
  previousSemesterRemaining: number;
};

type MessageGenerator = (ctx: PaymentMessageContext) => ReactNode;

// Messages mirror the banner's decision tree: instalments already paid this
// semester → firstPaymentMade; otherwise beforeFirstWithOutstanding /
// beforeFirst depending on whether a previous semester balance is still
// owed, keyed by billing state severity.
const MESSAGES: {
  success: ReactNode;
  fallback: { red: ReactNode; amber: ReactNode; blue: ReactNode };
  firstPaymentMade: { blue: MessageGenerator };
  beforeFirstWithOutstanding: {
    blue: MessageGenerator;
    amber: MessageGenerator;
    red: MessageGenerator;
  };
  beforeFirst: {
    blue: MessageGenerator;
    amber: MessageGenerator;
    red: MessageGenerator;
  };
} = {
  success: (
    <>
      <b>Payment up to date:</b> Your fees are up to date, ensuring
      uninterrupted access to your program.
    </>
  ),
  fallback: {
    red: (
      <>
        <b>Payment required.</b> Please make a payment.
      </>
    ),
    amber: (
      <>
        <b>Payment due soon.</b> Please make a payment.
      </>
    ),
    blue: (
      <>
        <b>Payment coming up.</b> Please make a payment.
      </>
    ),
  },
  firstPaymentMade: {
    blue: ({ currentSemesterRemaining }) => (
      <>
        <b>First payment made:</b> Pay{' '}
        <b>${currentSemesterRemaining.toFixed(2)}</b> by the end of the semester
        to ensure uninterrupted access to your program.
      </>
    ),
  },
  beforeFirstWithOutstanding: {
    red: ({ instalmentAmount, previousSemesterRemaining, formattedOverdueDate }) => (
      <>
        <b>Payment overdue:</b> Make your first payment of{' '}
        <b>${instalmentAmount.toFixed(2)}</b> and clear outstanding balance{' '}
        <b>${previousSemesterRemaining.toFixed(2)}</b> by{' '}
        <b>{formattedOverdueDate}</b> to avoid being withdrawn from your
        program.
      </>
    ),
    amber: ({ instalmentAmount, previousSemesterRemaining, formattedDueDate }) => (
      <>
        <b>Payment due soon:</b> Make your first payment of{' '}
        <b>${instalmentAmount.toFixed(2)}</b> and clear outstanding balance{' '}
        <b>${previousSemesterRemaining.toFixed(2)}</b> by{' '}
        <b>{formattedDueDate}</b> to advance to the next term.
      </>
    ),
    blue: ({ instalmentAmount, previousSemesterRemaining, formattedDueDate }) => (
      <>
        <b>Payment coming up:</b> Make your first payment of{' '}
        <b>${instalmentAmount.toFixed(2)}</b> and clear outstanding balance{' '}
        <b>${previousSemesterRemaining.toFixed(2)}</b> by{' '}
        <b>{formattedDueDate}</b> to advance to the next term.
      </>
    ),
  },
  beforeFirst: {
    red: ({ formattedOverdueDate }) => (
      <>
        <b>Payment overdue:</b> Make your first payment by{' '}
        <b>{formattedOverdueDate}</b> to avoid being withdrawn from your
        program.
      </>
    ),
    amber: ({ formattedDueDate }) => (
      <>
        <b>Payment due soon:</b> Make your first payment by{' '}
        <b>{formattedDueDate}</b> to complete your enrollment and begin the
        term.
      </>
    ),
    blue: ({ formattedDueDate }) => (
      <>
        <b>Payment coming up:</b> Make your first payment by{' '}
        <b>{formattedDueDate}</b> to complete your enrollment and begin the
        term.
      </>
    ),
  },
};

interface Props {
  paymentPlan?: GetProgramEnrollmentPaymentPlansQuery['viewer']['paymentPlans'][number];
  paymentFAQsLink: string;
  showPaymentPlanName?: boolean;
  showFAQLink?: boolean;
}

export const FitProgramEnrollmentPaymentBanner = ({
  paymentPlan,
  paymentFAQsLink,
  showPaymentPlanName = true,
  showFAQLink = true,
}: Props) => {
  const navigate = useNavigate();

  const { alertLevel, message, showBanner } = useMemo(() => {
    if (!paymentPlan) {
      return { showBanner: false };
    }
    if (
      !paymentPlan.status ||
      paymentPlan.status === PaymentPlanStatus.Cancelled
    ) {
      return { showBanner: false };
    }

    const {
      billingState,
      nextInstalmentDate,
      amountPerInstalment,
      currentSemesterPaidInstalments,
      currentSemesterRemainingAmount,
      previousSemesterRemainingAmount,
    } = paymentPlan;

    // If payment is completed or up to date, show success banner
    if (
      [
        BillingState.UpToDate,
        BillingState.InAdvance,
        BillingState.Completed,
      ].includes(billingState)
    ) {
      return {
        alertLevel: 'success' as AlertLevel,
        message: MESSAGES.success,
        showBanner: true,
      };
    }

    // Calculate due date from the plan's next instalment date, since the
    // payment service doesn't always maintain a next invoice to key off.
    const dueDate = nextInstalmentDate ? new Date(nextInstalmentDate) : null;
    if (!dueDate) {
      // Fallback to old logic if no due date
      const isError = [
        BillingState.InArrears,
        BillingState.Inactive,
        BillingState.Grace,
        BillingState.AtRisk,
        BillingState.Pending,
        BillingState.AwaitingPaymentConfirmation,
      ].includes(billingState);

      const level = isError ? 'red' : 'amber';
      return {
        alertLevel: level as AlertLevel,
        message: MESSAGES.fallback[level as 'red' | 'amber'],
        showBanner: true,
      };
    }
    const overdueDate = new Date(dueDate.valueOf() + 14 * TimeUnit.DAY);

    // Amounts come from the payment service in cents
    const instalmentAmount = (amountPerInstalment || 0) / 100;
    const currentSemesterRemaining =
      (currentSemesterRemainingAmount || 0) / 100;
    const previousSemesterRemaining =
      (previousSemesterRemainingAmount || 0) / 100;
    const ctx: PaymentMessageContext = {
      formattedDueDate: formatDate(dueDate),
      formattedOverdueDate: formatDate(overdueDate),
      instalmentAmount,
      currentSemesterRemaining,
      previousSemesterRemaining,
    };

    let level: AlertLevel = null;
    let bannerMessage: ReactNode = null;

    if (currentSemesterPaidInstalments) {
      if (currentSemesterRemaining) {
        level = 'blue';
        bannerMessage = MESSAGES.firstPaymentMade.blue(ctx);
      }
    } else if (previousSemesterRemaining) {
      if (billingState === BillingState.AtRisk) {
        level = 'red';
        bannerMessage = MESSAGES.beforeFirstWithOutstanding.red(ctx);
      } else if (billingState === BillingState.DueNow) {
        level = 'amber';
        bannerMessage = MESSAGES.beforeFirstWithOutstanding.amber(ctx);
      } else if (billingState === BillingState.DueSoon) {
        level = 'blue';
        bannerMessage = MESSAGES.beforeFirstWithOutstanding.blue(ctx);
      }
    } else {
      if (billingState === BillingState.AtRisk) {
        level = 'red';
        bannerMessage = MESSAGES.beforeFirst.red(ctx);
      } else if (billingState === BillingState.DueNow) {
        level = 'amber';
        bannerMessage = MESSAGES.beforeFirst.amber(ctx);
      } else if (billingState === BillingState.DueSoon) {
        level = 'blue';
        bannerMessage = MESSAGES.beforeFirst.blue(ctx);
      }
    }

    if (!level || !bannerMessage) {
      return { showBanner: false };
    }

    return {
      alertLevel: level,
      message: bannerMessage,
      showBanner: true,
    };
  }, [paymentPlan]);

  if (!showBanner) {
    return null;
  }

  const isError = alertLevel === 'red';
  const isSuccess = alertLevel === 'success';
  const isInfo = alertLevel === 'blue';
  const isWarning = alertLevel === 'amber';

  return (
    <div
      className={cn(
        'px-4 py-3 lg:py-1 flex flex-col sm:flex-row gap-2 lg:gap-4 items-center border w-full rounded-lg',
        isError ? 'border-border-error bg-destructive/10 text-destructive' : '',
        isSuccess ? 'border-success bg-success/10 text-success' : '',
        isInfo ? 'border-info bg-info/10 text-info' : '',
        isWarning ? 'border-warning bg-warning/10 text-warning' : ''
      )}
    >
      {isError && <AlertCircle className="stroke-destructive h-6 w-6" />}
      {isSuccess && <CheckCircle className="stroke-success min-h-6 min-w-6" />}
      {(isInfo || isWarning) && (
        <Info className="stroke-current min-h-6 min-w-6" />
      )}
      {showPaymentPlanName && <b>{paymentPlan?.definition?.name}</b>}
      <div className="text-sm">
        {message}{' '}
        {showFAQLink && !isSuccess && paymentPlan && (
          <span>
            Read our{' '}
            <Button
              className={`h-fit p-0 font-semibold underline ${
                isError
                  ? 'text-error'
                  : isWarning
                  ? 'text-warning'
                  : 'text-primary'
              }`}
              variant={'link'}
              onClick={() => navigate(paymentFAQsLink)}
            >
              payments FAQ
            </Button>
            .
          </span>
        )}
      </div>
      {paymentPlan?.billingState !== BillingState.Completed && (
        <Button
          variant={
            isError
              ? 'ghostDestructive'
              : isInfo
              ? 'ghostInfo'
              : isWarning
              ? 'ghostWarning'
              : 'ghostSuccess'
          }
          className="mx-auto sm:ml-auto sm:mr-0 p-0"
          onClick={() =>
            window.location.replace(
              paymentPlan?.viewUrl || ExternalApplicationLinks.payments
            )
          }
        >
          {isError && (paymentPlan ? 'Pay now' : 'Activate')}
          {isSuccess && 'Make a Payment'}
          {(isInfo || isWarning) && 'Make payment now'}
          <ChevronRight />
        </Button>
      )}
    </div>
  );
};
